import { useEffect, useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PauseCircle } from "lucide-react";

export default function PauseSeriesDialog({ job, onClose, onPaused }) {
  const [range, setRange] = useState({ start: "", end: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!job) return;
    const d = parseISO(job.scheduled_date);
    setRange({ start: format(d, "yyyy-MM-dd"), end: format(addDays(d, 14), "yyyy-MM-dd") });
  }, [job]);

  const pause = async () => {
    if (!range.start || !range.end) return toast.error("Pick both dates");
    if (range.end < range.start) return toast.error("'To' must be on or after 'From'");
    setSaving(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/pause`, range);
      toast.success(data.paused ? `${data.paused} visit${data.paused === 1 ? "" : "s"} paused` : "No scheduled visits in that range");
      onPaused(data.paused ? range : null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pause-series-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><PauseCircle className="w-5 h-5 text-brand" /> Pause series</DialogTitle>
          <DialogDescription>Scheduled visits between these dates are paused (kept on the calendar, no reminders). Resume them any time.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label className="label-caps text-muted-foreground">From</Label>
            <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="pause-start" /></div>
          <div className="space-y-2"><Label className="label-caps text-muted-foreground">To</Label>
            <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="pause-end" /></div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} data-testid="pause-cancel" className="rounded-sm">Cancel</Button>
          <Button onClick={pause} disabled={saving} data-testid="pause-confirm" className="rounded-sm">{saving ? "Pausing…" : "Pause visits"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
